import React from 'react';
import {useStoreActions} from 'easy-peasy';
import {Row, Col, Form} from 'react-bootstrap';
import * as yup from 'yup';
import {BaseSettingsForm, SelectField} from '@/components/forms';
import {Button} from '@/components/ui';
import useData from '@/utils/useData';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';

interface Channel {
    id: number;
    name: string;
    shortname: string;
    eventTypeId: number;
    active: boolean;
}

interface EventTypeChoice {
    value: number;
    label: string;
}

interface FormValues {
    name: string;
    shortname: string;
    eventTypeId: number;
    active: boolean;
}

interface ChannelSettingsFormProps {
    objId?: number;
    onSave: () => void;
}

interface ApiErrorResponse {
    key: string;
    message: string;
}

const channelSchema = yup.object({
    name: yup.string()
        .required(i18n.t('jt_channel_name_required.text')),
    shortname: yup.string()
        .required(i18n.t('jt_channel_shortname_required.text')),
    eventTypeId: yup.number()
        .required(i18n.t('jt_channel_event_type_required.text')),
});

const ChannelSettingsForm: React.FC<ChannelSettingsFormProps> = React.memo((
    {
        objId = 0,
        onSave = () => { },
    },
) => {
    const {showSuccess} = useAlert();
    const setSettingsModalOpen = useStoreActions((actions: any) => actions.settingsModalOpen.setSettingsModalOpen);
    const addOrEditAction = useStoreActions((actions: any) => actions.channels.addOrEdit);
    const setReloadChannels = useStoreActions((actions: any) => actions.channels.setReload);
    const [eventTypeFieldChoices] = useData<EventTypeChoice[]>(window.URLS.eventTypeFieldChoices, 'eventTypeFieldChoices');

    const initialValues: FormValues = {
        name: '',
        shortname: '',
        eventTypeId: eventTypeFieldChoices?.[0]?.value || 0,
        active: true,
    };

    const customErrorHandling = (error: any, formik: any) => {
        if (error.response?.status === 400) {
            const errorData = error.response?.data as ApiErrorResponse;
            if (errorData?.key === 'shortname') {
                formik.setFieldError('shortname', errorData?.message ?? i18n.t('invalid_data.text'));
                return true;
            } else if (errorData?.key === 'name') {
                formik.setFieldError('name', errorData?.message ?? i18n.t('invalid_data.text'));
                return true;
            }
        }
        return false;
    };

    const customSuccessHandling = (response: any, actionName: string) => {
        setSettingsModalOpen(false);
        addOrEditAction(response.data);

        if (objId) {
            showSuccess(i18n.t('to_channel_ch.text'));
        } else {
            showSuccess(i18n.t('to_channel_cr.text'));
        }

        setReloadChannels(true);
        onSave();
    };

    return (
        <BaseSettingsForm
            entityId={objId || undefined}
            resourceUrl={window.URLS.channelsResource}
            storeActionPath="channels.addOrEdit"
            validationSchema={channelSchema}
            initialValues={initialValues}
            successMessageKey="jt_save.text"
            onSave={onSave}
            customErrorHandling={customErrorHandling}
            customSuccessHandling={customSuccessHandling}
        >
            {(formik, loading) => (
                <>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="name" >
                                <Form.Label>{i18n.t('jt_channel_name.text')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formik.values.name}
                                    disabled={!!objId}
                                    isInvalid={formik.touched.name && !!formik.errors.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    name="name"
                                    autoFocus
                                    data-cy="channel-settings-name-input"
                                />
                                {!!objId && (
                                    <Form.Text style={{color: 'red'}}>
                                        {i18n.t('jt_contact_administrator.text')}
                                    </Form.Text>
                                )}
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="shortname" >
                                <Form.Label>{i18n.t('jt_channel_shortname.text')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="shortname"
                                    isInvalid={formik.touched.shortname && !!formik.errors.shortname}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.shortname}
                                    disabled={!!objId}
                                    data-cy="channel-settings-short-name-input"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.shortname}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="eventTypeId" >
                                <Form.Label>{i18n.t('jt_channel_event.text')}</Form.Label>
                                <SelectField
                                    name="eventTypeId"
                                    value={eventTypeFieldChoices?.find((x) => x.value === formik.values.eventTypeId) || null}
                                    onChange={(value) => formik.setFieldValue('eventTypeId', value?.value || 0)}
                                    options={eventTypeFieldChoices || []}
                                    isRequired={true}
                                    error={formik.errors.eventTypeId}
                                    touched={formik.touched.eventTypeId}
                                    isDisabled={!!objId}
                                />
                                {!!objId && (
                                    <Form.Text style={{color: 'red'}}>
                                        {i18n.t('jt_channel_event_type_cannot_be_changed_after_creation.text')}
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="active" >
                                <Form.Check
                                    name="active"
                                    label={i18n.t('es_active.text')}
                                    type="switch"
                                    onChange={formik.handleChange}
                                    checked={formik.values.active}
                                    isInvalid={!!formik.errors.active}
                                    feedback={formik.errors.active}
                                    id="channelActive"
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row className="mt-4">
                        <Col xs={12} className="d-flex justify-content-end">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                data-cy="channel-settings-save-btn"
                            >
                                {objId ? i18n.t('jt_update.text') : i18n.t('jt_save.text')}
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </BaseSettingsForm >
    );
});


export default ChannelSettingsForm; 